/**
 * Toast — minimal paper-aesthetic notification stack.
 *
 *   <ToastProvider> at the App root, then `useToast().showToast(...)` from any
 *   descendant. Stack lives top-right; auto-dismisses after `duration` ms (or
 *   8s for errors, 5s otherwise). Every toast has a × close button.
 *
 * Iter 3.1.2 — the stack is mounted into `document.body` via `createPortal`
 * so it lives in the root stacking context. Without the portal, the toast
 * region's `z-index: 1100` is bounded by whatever ancestor stacking context
 * the provider happens to sit inside (any ancestor with `transform`,
 * `filter`, `opacity < 1`, `position: fixed`, etc. starts a new context).
 * `Modal.tsx` already portals to `body` and stacks at z=1000 — without our
 * own portal the toast renders BELOW the modal even at a higher local
 * z-index. `createPortal` is React core, not an external library.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastInput {
  type?: ToastType;
  title: string;
  message?: string;
  /** Override default duration (ms). 0 disables auto-dismiss. */
  duration?: number;
  /**
   * Cycle 14 M3 — optional inline action button (e.g. "Oturumu kapat" for a
   * 403 reauth flow). Sits to the right of the message; dismisses the toast
   * after the click.
   */
  action?: ToastAction;
}

interface Toast extends Required<Omit<ToastInput, 'message' | 'duration' | 'action'>> {
  id: number;
  message?: string;
  duration: number;
  action?: ToastAction;
}

interface ToastContextValue {
  showToast: (input: ToastInput) => number;
  dismissToast: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION: Record<ToastType, number> = {
  info: 5000,
  success: 5000,
  warning: 6000,
  error: 8000,
};

const TYPE_ICON: Record<ToastType, string> = {
  info: 'i',
  success: '✓',
  warning: '!',
  error: '×',
};

const TYPE_TONE: Record<ToastType, { bg: string; fg: string; border: string }> = {
  info: {
    bg: 'oklch(96% 0.02 230)',
    fg: 'oklch(38% 0.13 230)',
    border: 'oklch(82% 0.08 230)',
  },
  success: {
    bg: 'oklch(96% 0.04 150)',
    fg: 'oklch(36% 0.14 150)',
    border: 'oklch(78% 0.10 150)',
  },
  warning: {
    bg: 'oklch(97% 0.05 80)',
    fg: 'oklch(40% 0.14 80)',
    border: 'oklch(82% 0.10 80)',
  },
  error: {
    bg: 'oklch(96% 0.04 25)',
    fg: 'oklch(40% 0.18 25)',
    border: 'oklch(78% 0.13 25)',
  },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);
  const timersRef = useRef(new Map<number, number>());

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    (input: ToastInput) => {
      const id = ++idRef.current;
      const type = input.type ?? 'info';
      const duration = input.duration ?? DEFAULT_DURATION[type];
      const toast: Toast = {
        id,
        type,
        title: input.title,
        ...(input.message !== undefined && { message: input.message }),
        ...(input.action !== undefined && { action: input.action }),
        duration,
      };
      setToasts((prev) => [...prev, toast]);
      if (duration > 0) {
        const timer = window.setTimeout(() => dismissToast(id), duration);
        timersRef.current.set(id, timer);
      }
      return id;
    },
    [dismissToast]
  );

  // Cleanup on unmount.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      timers.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({ showToast, dismissToast }),
    [showToast, dismissToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

function ToastStack({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) {
  // SSR-safe: skip the portal during a server render (no document) and let
  // the first client render put the stack in place. Vite's dev server is
  // CSR-only today, but this future-proofs the component if we ever
  // pre-render the shell.
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      role="region"
      aria-label="Bildirimler"
      style={{
        position: 'fixed',
        // Clear both shell topbar (always present) and editor topbar (present on
        // editor pages — non-editor pages just get a slightly larger gap, NBD).
        top: 'calc(var(--shell-topbar, 48px) + var(--editor-topbar, 40px) + 8px)',
        right: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        // Sit above modals (z=1000) so triple-error display surfaces above any
        // open dialog. Backdrop + dialog wrappers can occupy 1000–1099.
        // The portal hoists the stack into <body>, putting it in the root
        // stacking context — z-index alone wouldn't be enough (see header
        // comment + memory feedback_z_index_alone_doesnt_fix_stacking.md).
        zIndex: 1100,
        pointerEvents: 'none',
        maxWidth: 'min(380px, calc(100vw - 48px))',
      }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
  const tone = TYPE_TONE[toast.type];
  return (
    <div
      role={toast.type === 'error' || toast.type === 'warning' ? 'alert' : 'status'}
      style={{
        pointerEvents: 'auto',
        background: 'var(--surface)',
        border: `1px solid ${tone.border}`,
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-md)',
        padding: '10px 12px',
        display: 'grid',
        gridTemplateColumns: '20px 1fr auto',
        gap: 10,
        alignItems: 'start',
        fontSize: 13,
        color: 'var(--text)',
        animation: 'toast-in 140ms ease-out',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 20,
          height: 20,
          display: 'grid',
          placeItems: 'center',
          background: tone.bg,
          color: tone.fg,
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 700,
          fontFamily: 'var(--font-mono)',
          marginTop: 1,
        }}
      >
        {TYPE_ICON[toast.type]}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, lineHeight: 1.3 }}>{toast.title}</div>
        {toast.message && (
          <div
            style={{
              marginTop: 2,
              color: 'var(--text-2)',
              fontSize: 12,
              lineHeight: 1.4,
              wordBreak: 'break-word',
            }}
          >
            {toast.message}
          </div>
        )}
        {toast.action && (
          <button
            type="button"
            onClick={() => {
              toast.action!.onClick();
              onDismiss(toast.id);
            }}
            style={{
              marginTop: 8,
              appearance: 'none',
              background: 'transparent',
              border: '1px solid var(--border-strong)',
              borderRadius: 'var(--radius-sm)',
              padding: '4px 10px',
              fontSize: 12,
              fontFamily: 'inherit',
              fontWeight: 500,
              color: 'var(--text)',
              cursor: 'pointer',
            }}
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Kapat"
        title="Kapat"
        style={{
          appearance: 'none',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--text-3)',
          fontSize: 16,
          lineHeight: 1,
          padding: 2,
          marginTop: -2,
        }}
      >
        ×
      </button>
    </div>
  );
}
